import { AutocompleteRenderOptionState, Box, Grid, Stack, styled } from '@mui/material';
import { Category } from '@synergy/models';
import { ChipAutocomplete, PoolButton, PoolModal, PoolTypography } from '@synergy/components';
import { Field, FieldProps, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@mui/styles';
import { shouldForwardPropExclude } from '@synergy/utils';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import { CategoryFormikValues } from '../models/category-formik-values';
import { ConfigurationDetailForm } from '../../components/configuration-detail-form';
import { Eclass } from '../../../../common/models/eclass';
import { categoriesModalValidationSchema } from './categories-modal-validation-schema';
import { createCategory, patchCategory } from '../../../../common/actions/category-actions';
import { eclassSelector } from '../../../../common/selectors/eclass-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const useStyles = makeStyles(() => ({
    actionChildren: {
        minWidth: 200,
    },
}));

interface CategoriesDetailModalProps {
    visible: boolean;
    category?: Category;
    depth?: number;
    unavailableEclasses?: number[];
    onClose: () => void;
}

const Highlighted = styled('span', { shouldForwardProp: shouldForwardPropExclude('highlight') })<{
    highlight: boolean;
}>(({ theme, highlight }) => ({
    color: highlight ? theme.palette.primary.main : undefined,
    whiteSpace: 'pre',
}));

export const CategoriesDetailModal = (props: CategoriesDetailModalProps) => {
    const dispatch = useThunkDispatch();
    const classes = useStyles();
    const allEclasses = useAppSelector(eclassSelector);
    const currentLocale = useAppSelector(state => state.currentLocale);
    const { visible, category, depth = Number.MAX_SAFE_INTEGER, unavailableEclasses = [], onClose } = props;
    const eclassOptions = allEclasses.filter(eclass => !unavailableEclasses.includes(eclass.id));

    const handleClose = (formikProps: FormikProps<CategoryFormikValues>) => {
        formikProps.resetForm();
        onClose();
    };

    const handleSubmit = (values: CategoryFormikValues, formikProps: FormikHelpers<CategoryFormikValues>) => {
        onClose();

        if (category?.id && category.id >= 0) {
            const nextCategory: Category = {
                id: category.id,
                translations: values.translations,
                isVisibleInShop: category.isVisibleInShop,
                parentId: category.parentId,
                eclassPositions: values.eclasses.map(eclass => eclass.id),
            };

            dispatch(patchCategory(nextCategory));
        } else {
            dispatch(createCategory(values));
        }

        formikProps.resetForm();
    };

    const isNewCategory = !category?.id || category.id <= 0;
    const initialValues: CategoryFormikValues = {
        parentId: category?.parentId,
        translations: {
            [currentLocale]: '',
            ...category?.translations,
        },
        eclasses: (category?.eclassPositions ?? [])
            .map(id => eclassOptions.find(eclass => eclass.id === id))
            .filter(x => !!x) as Eclass[],
    };

    const actionChildren = (formikProps: FormikProps<CategoryFormikValues>) => (
        <>
            <PoolButton
                key="1"
                styleType="firstLevel"
                variant="text"
                onClick={() => handleClose(formikProps)}
                className={classes.actionChildren}
            >
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>
            <PoolButton
                key="2"
                styleType="firstLevel"
                onClick={() => formikProps.submitForm()}
                className={classes.actionChildren}
                disabled={Object.keys(formikProps.values.translations).length === 0}
            >
                <FormattedMessage id={isNewCategory ? 'app.common.add' : 'app.common.save'} />
            </PoolButton>
        </>
    );

    const eclassRenderOption = (eclass: Eclass, state: AutocompleteRenderOptionState) => {
        const labelMatches = match(eclass.label, state.inputValue);
        const labelParts = parse(eclass.label, labelMatches);

        return (
            <Box>
                {labelParts.map((labelPart, index) => (
                    <Highlighted key={index} highlight={labelPart.highlight}>
                        {labelPart.text}
                    </Highlighted>
                ))}
            </Box>
        );
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            validationSchema={categoriesModalValidationSchema(currentLocale)}
        >
            {(formikProps: FormikProps<CategoryFormikValues>) => (
                <Form>
                    <PoolModal
                        title={
                            <FormattedMessage
                                id={
                                    isNewCategory
                                        ? 'app.configuration.createCategory'
                                        : 'app.configuration.editCategory'
                                }
                            />
                        }
                        isVisible={visible}
                        handleClose={() => handleClose(formikProps)}
                        actionChildren={actionChildren(formikProps)}
                    >
                        <Stack gap={4}>
                            <Box>
                                <PoolTypography styleType="sectionTitleNew" id="app.label.link" />
                                <Grid container={true} spacing={2} sx={{ mt: 1 }}>
                                    <Grid item={true} xs={12}>
                                        <Field name="eclasses">
                                            {({ field, form }: FieldProps<Eclass[]>) => (
                                                <ChipAutocomplete
                                                    noOptionsText={<FormattedMessage id="app.common.emptyResult" />}
                                                    options={eclassOptions}
                                                    getOptionLabel={eclass => eclass.label}
                                                    renderOption={eclassRenderOption}
                                                    getChipLabel={eclass => eclass.label}
                                                    onChange={(_, value) => form.setFieldValue(field.name, value)}
                                                    value={field.value}
                                                    textfieldPlaceholder="app.label.addeclass"
                                                    limitTags={1}
                                                    disabled={depth > 1}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Box>
                                <PoolTypography styleType="sectionTitleNew" id="app.table.column.translation" />
                                <ConfigurationDetailForm />
                            </Box>
                        </Stack>
                    </PoolModal>
                </Form>
            )}
        </Formik>
    );
};
